import { Message } from "google-protobuf";
import _ from "lodash";

import { LcmMsg, LcmType } from "@skydio/lcm/src";
import { tunnel_channel_t } from "@skydio/lcm/types/skybus_tunnel/tunnel_channel_t";

import { SKYBUS_WS_SUBSCRIBE } from "@skydio/channels/src/skybus_ws_subscribe";
import { SKYBUS_WS_UNSUBSCRIBE } from "@skydio/channels/src/skybus_ws_unsubscribe";
import { VEHICLE_BATTERY_PREDICTION } from "@skydio/channels/src/vehicle_battery_prediction";
import { FLIGHT_PHASE } from "@skydio/channels/src/flight_phase";
import { legacy_15561678412280274150_battery_prediction_t } from "@skydio/lcm/types/flight_deck/legacy_15561678412280274150_battery_prediction_t";
import { legacy_255861669266133548_flight_phase_t } from "@skydio/lcm/types/common/legacy_255861669266133548_flight_phase_t";

import { WEBRTC_SUBSCRIPTION_LIST_PB } from "@skydio/channels/src/webrtc_subscription_list_pb";
import { WEBRTC_SUBSCRIPTION_ACK_PB } from "@skydio/channels/src/webrtc_subscription_ack_pb";

import {
  SubscribedChannel,
  SubscribedChannelList,
  SubscriptionAck,
} from "@skydio/pbtypes/pbtypes/infrastructure/skybus_tunnel/tunnel_pb";
import { broadcast_channel_t } from "@skydio/lcm/types/iot_api/broadcast_channel_t";

import { Connector } from "./transport/base";
import { QOS, isLcmType, isPbType, MakesMessage, WireMsg, WireType } from "./types";
import LivekitConnector from "./transport/livekit/livekit_connector";
import WebsocketConnector from "./transport/websocket/websocket_connector";
import { logger } from "./logger";
import WebsocketTransportLayer from "./transport/websocket/websocket_transport_layer";

const LIST_REPUBLISH_DELAY_MS = 2000;
const CLEANUP_DELAY_MS = 5000;
const DEFAULT_NOMINAL_DT = 1.0 / 15.0;
const DEFAULT_THROTTLE_DT = 1.0 / 2.0;
// After we unsubscribe from a channel, we will tolerate receiving a certain number of messages before
// we schedule a list republishing to the vehicle. This is done to avoid unnecessary subscription
// list retransmissions during subscribe/unsubscribe bursts caused by makeRequest API
const MAX_UNSUBSCRIBED_MESSAGES_PER_CHANNEL = 10;

export type SkybusSubscription<D extends WireMsg> = Required<{
  channel: string;
  type: LcmType<Exclude<D, Message>> | MakesMessage<Exclude<D, LcmMsg>>;
  callback: (channel: string, message: D) => void;
}>;

/**
 * We have 2 separate implementations, so it makes sense to generalize the subscription list handling
 */
interface SkybusSubscriptionList {
  add(subscription: SkybusSubscription<WireMsg>, onAccepted?: () => void): void;
  messageReceived(channel: string): void;
  get(channel: string): SkybusSubscription<WireMsg> | undefined;
  remove(channel: string): boolean;
  republish(): void;
  dispose(): void;
}

/**
 * Subscription list handling for web socket implementation
 */
class SubListWS implements SkybusSubscriptionList {
  private subscriptions: Map<string, SkybusSubscription<WireMsg>>;
  private connector: WebsocketConnector;

  constructor(connector: WebsocketConnector) {
    this.subscriptions = new Map();
    this.connector = connector;
  }

  add(subscription: SkybusSubscription<WireMsg>, onAccepted?: () => void) {
    this.subscriptions.set(subscription.channel, subscription);
    this.connector
      .publish(
        SKYBUS_WS_SUBSCRIBE.channel,
        new SKYBUS_WS_SUBSCRIBE.type({
          channel: subscription.channel,
        })
      )
      .then(() => {
        logger.log(`Subscribed to ${subscription.channel}`);
        onAccepted?.();
      });
  }

  get(channel: string) {
    return this.subscriptions.get(channel);
  }

  messageReceived(channel: string): void {
    // no-op
  }

  remove(channel: string) {
    if (!this.subscriptions.has(channel)) return false;

    this.subscriptions.delete(channel);
    this.connector.publish(SKYBUS_WS_UNSUBSCRIBE, new SKYBUS_WS_UNSUBSCRIBE.type({ channel }));
    return true;
  }

  republish() {
    this.subscriptions.forEach(sub => {
      this.connector.publish(
        SKYBUS_WS_SUBSCRIBE,
        new SKYBUS_WS_SUBSCRIBE.type({ channel: sub.channel })
      );
    });
  }

  dispose() {
    this.subscriptions.forEach(sub => {
      this.connector.publish(
        SKYBUS_WS_UNSUBSCRIBE,
        new SKYBUS_WS_UNSUBSCRIBE.type({ channel: sub.channel })
      );
    });
    this.subscriptions.clear();
  }
}

/**
 * Subscription list handling for livekit implementation
 */
class SubListLivekit implements SkybusSubscriptionList {
  private tunnelChannels: Record<string, tunnel_channel_t>;
  private broadcastChannels: Set<string>;

  private subscriptions: Map<string, SkybusSubscription<WireMsg>>;
  private connector: LivekitConnector;
  private nonce: number = 1;
  private subscriptionAck?: SubscriptionAck;

  // a queue of subscription confirmation callbacks along with the nonce they were published with
  private pendingConfirmationQueue: Array<{ nonce: number; callback: () => void }> = [];
  // Count unwanted messages on unsubscribed channels
  private unsubscribedMessagesReceived = new Map<string, number>();
  // Track configuration warnings to prevent spam
  private warnedForConfig = new Set<string>();

  private publishTimeoutId?: NodeJS.Timeout;
  private republishTimeoutId?: NodeJS.Timeout;
  private cleanupTimeoutId?: NodeJS.Timeout;

  constructor(
    connector: LivekitConnector,
    tunnelChannelList: tunnel_channel_t[] = [],
    broadcastChannelList: broadcast_channel_t[] = []
  ) {
    this.subscriptions = new Map();
    this.connector = connector;

    this.broadcastChannels = broadcastChannelList.reduce((broadcastChannels, broadcastChannel) => {
      broadcastChannels.add(broadcastChannel.channel);
      return broadcastChannels;
    }, new Set<string>());

    this.tunnelChannels = tunnelChannelList.reduce(
      (all, channel) => ({
        ...all,
        [channel.channel]: channel,
      }),
      {}
    );

    // sets up a listener on the subscription confirmation channel for livekit
    // this will always be on and enables us to know when our subscription requests worked
    const { channel, type } = WEBRTC_SUBSCRIPTION_ACK_PB;
    this.subscriptions.set(channel, {
      channel,
      type,
      // @ts-ignore
      callback: this.onSubscriptionListAcknowledged,
    });
  }

  add(subscription: SkybusSubscription<WireMsg>, onAccepted?: () => void) {
    if (this.subscriptions.has(subscription.channel)) {
      onAccepted?.();
    } else {
      this.scheduleSubscription(subscription.channel, onAccepted);
    }

    this.unsubscribedMessagesReceived.delete(subscription.channel);

    // @ts-ignore
    this.subscriptions.set(subscription.channel, subscription);
  }

  get(channel: string) {
    return this.subscriptions.get(channel);
  }

  messageReceived(channel: string) {
    const subscription = this.subscriptions.get(channel);
    if (!subscription) {
      logger.trace("got unrequested message", { channel });
      return;
    }

    // If we actually receive an unwanted message on a previously unsubscribed channel,
    // we should republish the subscription list to the vehicle
    if (this.unsubscribedMessagesReceived.has(channel) && !this.broadcastChannels.has(channel)) {
      const count = this.unsubscribedMessagesReceived.get(channel) ?? 0;
      this.unsubscribedMessagesReceived.set(channel, count + 1);
      if (count > MAX_UNSUBSCRIBED_MESSAGES_PER_CHANNEL) {
        this.scheduleCleanup();
      }
    }
  }

  remove(channel: string) {
    if (!this.subscriptions.has(channel)) return false;
    this.unsubscribedMessagesReceived.set(channel, 0);
    return true;
  }

  republish() {
    this.publishSubscriptionList();
  }

  dispose() {
    // Kill all the timeouts
    [this.publishTimeoutId, this.republishTimeoutId, this.cleanupTimeoutId].forEach(
      id => id ?? clearTimeout(id)
    );

    this.subscriptions.clear();

    //Publish
    this.publishSubscriptionList();
  }

  private onSubscriptionListAcknowledged = (_channel: any, msg: SubscriptionAck) => {
    logger.log(
      "Subscription list acknowledged for nonce",
      msg.getNonce(),
      `for device ${(this.connector as LivekitConnector).deviceType}`
    );

    // if we have subscription confirmations pending confirmation, confirm all
    // the ones for which their nonce was before the currently acknowledged one
    while (
      this.pendingConfirmationQueue.length &&
      this.pendingConfirmationQueue[0].nonce <= msg.getNonce()
    ) {
      this.pendingConfirmationQueue.shift()?.callback();
    }
    // make sure we have the ack for the most recent nonce
    if (this.nonce - 1 === msg.getNonce()) {
      this.subscriptionAck = msg;
    }
  };

  private scheduleSubscription = (channel: string, onComplete?: () => void) => {
    // Add callback to the list if provided
    if (onComplete) {
      this.pendingConfirmationQueue.push({
        nonce: this.nonce,
        callback: onComplete,
      });
    }

    // Remove counter for this channel since we are resubscribing
    this.unsubscribedMessagesReceived.delete(channel);

    // Schedule list publish on next frame.
    // prevents publishing multiple requests if multiple subscriptions are added in a loop
    if (this.publishTimeoutId) {
      clearTimeout(this.publishTimeoutId);
    }

    this.publishTimeoutId = setTimeout(() => {
      this.publishSubscriptionList();
    }, 1);
  };

  /**
   * Eventually we'll send an updated list with unsubscribed channels taken into account
   */
  private scheduleCleanup() {
    if (this.cleanupTimeoutId) {
      clearTimeout(this.cleanupTimeoutId);
    }

    this.cleanupTimeoutId = setTimeout(() => {
      if (this.unsubscribedMessagesReceived.size === 0) {
        return;
      }
      this.unsubscribedMessagesReceived.forEach((_, key) => {
        this.subscriptions.delete(key);
      });

      this.unsubscribedMessagesReceived.clear();
      this.publishSubscriptionList();
    }, CLEANUP_DELAY_MS);
  }

  /**
   * Actually sends a subscription list to the vehicle
   * @param firstAttempt - indicates if this is a first attempt or a retry
   * @returns
   */
  private async publishSubscriptionList(firstAttempt = true) {
    // clear previous acknowledgement on first call
    if (firstAttempt) {
      this.subscriptionAck = undefined;
    }

    // Already acknowledged
    if (this.subscriptionAck) return;

    // No need to publish the list in broadcast-only mode
    if (this.connector.options.broadcastOnly) return;

    // Shouldn't publish if not connected
    if (!this.connector.transport.isConnected) return;

    const subscriptionList = this.getSubscriptionList();

    logger.log(
      `Publishing subscription list with nonce ${subscriptionList.getNonce()}:`,
      subscriptionList.getChannelsList().map(sub => sub.getChannel()),
      `for device ${(this.connector as LivekitConnector).deviceType}`,
      this.connector
    );

    await this.connector.publish(WEBRTC_SUBSCRIPTION_LIST_PB, subscriptionList, {
      qos: QOS.UNRELIABLE,
    });

    // Schedule retry
    if (this.republishTimeoutId) clearTimeout(this.republishTimeoutId);
    this.republishTimeoutId = setTimeout(() => {
      this.publishSubscriptionList(false);
    }, LIST_REPUBLISH_DELAY_MS);
  }

  private getSubscriptionList() {
    const subscriptionList = new SubscribedChannelList();
    this.subscriptions.forEach(sub => {
      // avoid adding broadcast channels to the subscription list to avoid duplicating
      // data sent over data channels
      if (this.broadcastChannels.has(sub.channel)) {
        return;
      }

      const subscribedChannel = new SubscribedChannel();
      subscribedChannel.setChannel(sub.channel);
      if (sub.channel in this.tunnelChannels) {
        const tunnelChannel = this.tunnelChannels[sub.channel];
        // @ts-ignore
        subscribedChannel.setDownsampleNominalDt(tunnelChannel.downsample_nominal_dt);
        // @ts-ignore
        subscribedChannel.setDownsampleLowThrottleDt(tunnelChannel.downsample_low_throttle_dt);

        // @ts-ignore
        subscribedChannel.setQos(tunnelChannel.qos.value as number);
      } else {
        subscribedChannel.setDownsampleNominalDt(DEFAULT_NOMINAL_DT);
        subscribedChannel.setDownsampleLowThrottleDt(DEFAULT_THROTTLE_DT);
        if (!this.warnedForConfig.has(sub.channel)) {
          logger.error(
            `No tunnel channel configuration found for ${sub.channel}, using`,
            `default downsampling ${subscribedChannel.getDownsampleNominalDt()} and throttling ${subscribedChannel.getDownsampleLowThrottleDt()}}.`,
            "Channels should be explicitly specified in the tunnel channel list in ",
            "params/channel_groups/params.py (browser_to_vehicle_channels or browser_to_dock_channels)"
          );
          this.warnedForConfig.add(sub.channel);
        }
      }
      subscriptionList.addChannels(subscribedChannel);
    });
    if (this.connector instanceof LivekitConnector) {
      subscriptionList.setClientId(this.connector.transport.clientId);
      subscriptionList.setNonce(this.nonce);

      this.nonce++;
      return subscriptionList;
    } else {
      throw new Error("Subscription lists only supported over WebRTC for now.");
    }
  }
}

export default class SkybusLive {
  public lastMessageUtime?: number;

  private subscriptions: SkybusSubscriptionList;

  constructor(
    readonly connector: Connector,
    tunnelChannelList: tunnel_channel_t[] = [],
    broadcastChannelList: broadcast_channel_t[] = []
  ) {
    connector.registerOnMessageCallback(this.handleMessage.bind(this));

    if (connector instanceof LivekitConnector) {
      this.subscriptions = new SubListLivekit(connector, tunnelChannelList, broadcastChannelList);
    } else if (connector instanceof WebsocketConnector) {
      this.subscriptions = new SubListWS(connector);
      if (connector.transport instanceof WebsocketTransportLayer) {
        connector.transport.onSocketReconnected = this.republishExistingSubscriptions.bind(this);
      }
    }
  }

  /**
   * Subscribe to a channel with a callback that takes the channel and message.
   * Only one subscription is allowed per channel! Muxing is done at unified skybus level;
   * a single subscription can have multiple listeners.
   * @param {string} channel
   * @param {function} callback
   */
  public subscribe<D extends WireMsg>(
    channel: string,
    type: WireType<D>,
    callback: SkybusSubscription<D>["callback"],
    onSubscriptionAccepted?: () => void
  ): void {
    const subscription: SkybusSubscription<D> = {
      channel,
      type,
      callback,
    };

    //@ts-ignore
    this.subscriptions.add(subscription, onSubscriptionAccepted);
  }

  /**
   * Unsubscribe from a clientId returned from subscribe
   *
   * @param {string} channel
   */
  public unsubscribe(channel: string): boolean {
    return this.subscriptions.remove(channel);
  }

  private handleMessage(channel: string, encodedMsg: Uint8Array): boolean {
    this.subscriptions.messageReceived(channel);
    const subscription = this.subscriptions.get(channel);
    if (subscription === undefined) return false;

    if (isLcmType(subscription.type)) {
      try {
        // HACK(grace): if the channel is VEHICLE_BATTERY_PREDICTION, use the legacy channel to
        // decode the message. A change to the battery_prediction_t lcm was merged which broke battery
        // viewing on livestreams for vehicles on older versions. Decoding based on the legacy type will always
        // work, since devices that are actually publishing the new type have translations on broadcast channels,
        // meaning we'll receive VEHICLE_BATTERY_PREDICTION_PB.
        // HACK(kp): did the same thing for FLIGHT_PHASE
        let msg;

        switch (subscription.channel) {
          case VEHICLE_BATTERY_PREDICTION.channel:
            msg = new legacy_15561678412280274150_battery_prediction_t().decode(encodedMsg);
            break;
          case FLIGHT_PHASE.channel:
            msg = new legacy_255861669266133548_flight_phase_t().decode(encodedMsg);
            break;
          default:
            msg = new subscription.type().decode(encodedMsg);
            break;
        }

        subscription.callback(channel, msg);
      } catch (e) {
        logger.log(`Could not decode LCM message on channel ${channel}`);
        logger.error(e);
      }
    } else if (isPbType(subscription.type)) {
      const pbMsg = subscription.type(encodedMsg);
      this.lastMessageUtime = _.get(pbMsg.toObject(), "utime");
      subscription.callback(channel, pbMsg);
    }
    return true;
  }

  public republishExistingSubscriptions(qosValue?: tunnel_channel_t["qos"]["value"]) {
    logger.log("Republishing existing subscriptions");
    this.subscriptions.republish();
  }

  public setBroadcastOnlyMode(value: boolean) {
    this.connector.options.broadcastOnly = value;
    if (!value) {
      this.republishExistingSubscriptions();
    }
  }

  public close() {
    logger.log("Closing SkybusLive");
    this.subscriptions.dispose();
    this.connector.transport.close();
  }
}
